<template>
    <div class="modal fade" id="contract-template-create-modal" tabindex="-1" aria-labelledby="contract-template-create-modal" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-fullscreen contract-template-dialog">
            <div class="modal-content modern-modal">
                <div class="modal-header border-0">
                    <div>
                        <h5 class="modal-title mb-0">
                            <AppIcon name="bi-file-earmark-richtext" class="bi bi-file-earmark-richtext me-2" />
                            {{ contract.id ? $t('edit_template') : $t('create_contract_template') }}
                        </h5>
                    </div>
                    <button type="button" class="btn-close" @click="hide" aria-label="close"></button>
                </div>

                <div class="modal-body pt-0 contract-template-modal-body">
                    <ContractSectionBuilder
                        ref="contractBuilder"
                        v-model="contract.content"
                        :variables="availableVariables"
                        :variableValues="previewValues"
                        allow-smart-variables
                        :pagePadding.sync="pagePadding"
                        :pageMargin.sync="pageMargin"
                    >
                        <template v-slot:sidebar-top>
                            <div class="template-name-card">
                                <label class="form-label small fw-semibold mb-2">
                                    {{ $t('template_name') }} <span class="text-danger">*</span>
                                </label>
                                <input
                                    v-model="contract.title"
                                    type="text"
                                    class="form-control"
                                    :placeholder="$t('template_name_placeholder')"
                                >
                            </div>
                        </template>
                    </ContractSectionBuilder>
                </div>

                <div class="modal-footer border-0">
                    <button type="button" class="btn btn-light" @click="hide">
                        <AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{ $t('close') }}
                    </button>
                    <button type="button" class="btn btn-success px-4" :disabled="loading || !isValid" @click="save">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <AppIcon name="bi-check-circle" v-else class="bi bi-check-circle me-2" />
                        {{ contract.id ? $t('update_template') : $t('save_template') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import helper from '../../helper'
import ContractSectionBuilder from './ContractSectionBuilder.vue'

export default {
  name: 'AddEditContractTemplateModal',
  components: {
    ContractSectionBuilder
  },
  props: ['contractData'],
  data(){
    return {
        pagePadding: 20,
        pageMargin: 20,
        contract: {
            id: null,
            title: '',
            contract_number: '',
            type: 'client',
            linked_id: '',
            start_date: new Date().toISOString().substr(0, 10),
            end_date: '',
            amount: 0,
            status: 'active',
            billing_frequency: 'monthly',
            auto_renew: false,
            content: '',
            is_template: true
        },
        createModal: null,
        loading: false,
        availableVariables: [
            { key: '{{business.name}}', label: 'Business Name' },
            { key: '{{business.email}}', label: 'Business Email' },
            { key: '{{business.phone}}', label: 'Business Phone' },
            { key: '{{business.address}}', label: 'Business Address' },
            { key: '{{contract.contract_number}}', label: 'Contract Number' },
            { key: '{{contract.title}}', label: 'Contract Title' },
            { key: '{{contract.start_date}}', label: 'Start Date' },
            { key: '{{contract.end_date}}', label: 'End Date' },
            { key: '{{contract.amount}}', label: 'Amount' },
            { key: '{{entity.name}}', label: 'Entity Name' },
            { key: '{{entity.email}}', label: 'Entity Email' },
            { key: '{{entity.phone}}', label: 'Entity Phone' },
            { key: '{{entity.address}}', label: 'Entity Address' },
            { key: '{{employee.hire_date}}', label: 'Employee Hire Date' },
            { key: '{{current_date}}', label: 'Current Date' },
            { key: '{{date}}', label: 'Current Date' }
        ]
    }
  },
  computed: {
    isValid() {
        return this.contract.title;
    },
    previewValues() {
        const business = this.$store.getters.BUSINESS || {};
        const currentDate = this.formatVariableDate(new Date());
        return {
            '{{business.name}}': business.name || this.$t('business_name'),
            '{{business.email}}': business.email || this.$t('email'),
            '{{business.phone}}': business.phone || this.$t('phone'),
            '{{business.address}}': business.address || this.$t('address'),
            '{{contract.contract_number}}': 'CTR-001',
            '{{contract.title}}': this.contract.title || this.$t('contract'),
            '{{contract.start_date}}': this.formatVariableDate(this.contract.start_date),
            '{{contract.end_date}}': this.formatVariableDate(this.contract.end_date),
            '{{contract.amount}}': this.contract.amount || '0',
            '{{employee.hire_date}}': currentDate,
            '{{current_date}}': currentDate,
            '{{date}}': currentDate
        };
    }
  },
  watch: {
    contractData: {
        handler(val) {
            if (val) {
                this.loadContract(val);
            } else {
                this.resetForm();
            }
        },
        immediate: true
    }
  },
  mounted(){
    // eslint-disable-next-line no-undef
    this.createModal = new bootstrap.Modal(document.getElementById('contract-template-create-modal'), {backdrop: 'static', keyboard: false})
    
    document.getElementById('contract-template-create-modal').addEventListener('hidden.bs.modal', () => { 
        this.$emit('closed');
    })
  },
  methods: {
    formatVariableDate(date) {
        return date ? helper.formatDate(date, false, 'DD/MM/YYYY') : '';
    },
    show() {
        this.resetForm(); // Ensure clean state
        if (this.contractData) {
             this.loadContract(this.contractData);
        } else {
             this.contract.is_template = true;
             this.$nextTick(() => {
                 if (this.$refs.contractBuilder) this.$refs.contractBuilder.applyPreset('blank');
             });
        }

        this.createModal.show();
    },
    hide() {
        this.createModal.hide();
    },
    loadContract(data) {
        this.contract = { ...data };
        this.pagePadding = 20;
        this.pageMargin = 20;

        if (this.contract.content && this.contract.content.includes('contract-page-wrapper')) {
            const parser = new DOMParser();
            const document = parser.parseFromString(this.contract.content, 'text/html');
            const wrapper = document.querySelector('.contract-page-wrapper');
            if (wrapper) {
                const parsedPadding = parseInt(wrapper.style.padding, 10);
                const parsedMargin = parseInt(wrapper.style.margin, 10);
                this.pagePadding = Number.isNaN(parsedPadding) ? 20 : parsedPadding;
                this.pageMargin = Number.isNaN(parsedMargin) ? 20 : parsedMargin;
                this.contract.content = wrapper.innerHTML;
            }
        }
    },
    resetForm() {
        this.pagePadding = 20;
        this.pageMargin = 20;
        this.contract = {
            id: null,
            title: '',
            contract_number: '',
            type: 'client',
            linked_id: '',
            start_date: new Date().toISOString().substr(0, 10),
            end_date: '',
            amount: 0,
            status: 'active',
            billing_frequency: 'monthly',
            auto_renew: false,
            content: '',
            is_template: true
        };
    },
    async save(){
        try {
            const builder = this.$refs.contractBuilder;
            if (builder && typeof builder.flushStructuredEdits === 'function') {
                builder.flushStructuredEdits();
            }
            this.loading = true;
            
            // Wrap content
            const wrappedContent = `<div class="contract-page-wrapper" style="padding: ${this.pagePadding}px; margin: ${this.pageMargin}px;">${this.contract.content}</div>`;
            const payload = { ...this.contract, content: wrappedContent };
            
            if (!payload.end_date) payload.end_date = null;
            if (!payload.linked_id) payload.linked_id = '';

            let response;
            if (this.contract.id) {
                response = await helper.customPatch(`${process.env.VUE_APP_API}/contracts/${this.contract.id}`, payload, {showToast: true});
            } else {
                delete payload.id;
                response = await helper.customPost(`${process.env.VUE_APP_API}/contracts`, payload, {showToast: true});
            }
            
            this.loading = false;
            this.$emit('saved', response.data);
            this.hide();
        } catch (error) {
            this.loading = false;
            alert(error);
        }
    }
  }
}
</script>

<style scoped>
.contract-template-dialog {
    width: 100vw;
    max-width: none;
    height: 100vh;
    margin: 0;
}

.contract-template-dialog .modal-content {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    border: 0;
    border-radius: 0;
}

.contract-template-dialog .modal-header {
    flex: 0 0 auto;
    padding: 16px 24px;
}

.contract-template-dialog .modal-footer {
    flex: 0 0 auto;
    min-height: 68px;
    padding: 12px 24px;
    background: #fff;
    border-top: 1px solid #e7eaf0 !important;
    box-shadow: 0 -5px 18px rgba(31, 38, 55, 0.04);
}

.contract-template-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    padding: 0 24px 20px;
    overflow-y: auto;
    background: #f8f9fc;
}

.template-name-card {
    padding: 12px;
    background: #fff;
    border: 1px solid #e5e8f0;
    border-radius: 12px;
}

@media (max-width: 767.98px) {
    .contract-template-dialog .modal-header,
    .contract-template-dialog .modal-footer,
    .contract-template-modal-body {
        padding-right: 14px;
        padding-left: 14px;
    }
}
</style>
